import HeaderPortal from "components/portal/header-portal";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/components/dialog";
import { Button } from "ui/components/button";
import { TIMESHEET_LOG_DAY_VIEW, formatDateForLogOptions } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form/index";
import { Input } from "ui/components/input";
import { Textarea } from "ui/components/textarea";
import TimesheetSelect from "@/components/common-select";
import { dateOptions } from "@/modules/timesheet/logs/log/create/utils";
import { timesheetLogSchema } from "@/modules/timesheet/logs/log/create/schema";
import { eventBus } from "utils/eventBus";
import { useForm, useFieldArray } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useLockTimesheet } from "components/timesheet/lock/useLock";
import { useLogTimesheetEntries } from "components/timesheet/log/mutation";
import useRemoveSearchParam from "@/hooks/useRemoveSearchParams";
import useTimesheetData from "components/timesheet/log/useTimesheetData";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "components/index";
import { DELETE, PLUS } from "icons/src";

export default function TimesheetLog({
  command = false,
  initialValues,
  onSuccess,
  onDelete,
  created_from_draft = false,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isNew, setIsNew] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentDate, setCurrentDate] = useState(null);
  const date = searchParams.get("date");
  const navigate = useNavigate();

  const logTimesheetMutation = useLogTimesheetEntries();

  const form = useForm({
    resolver: zodResolver(timesheetLogSchema),
    defaultValues: {
      date: "",
      project: "",
      job: "",
      milestone: "",
      entries: [
        {
          tag: "",
          logged_hours: "",
          description: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "entries",
  });

  const { canEdit } = useLockTimesheet({ date: currentDate });

  const {
    projects = [],
    jobs = [],
    milestones = [],
    tag = [],
  } = useTimesheetData(form.getValues("date"), form.watch(), true);

  useEffect(() => {
    if (date) {
      form.setValue("date", date);
      setCurrentDate(date);
    }
  }, [date, form.getValues("date")]);

  useEffect(() => {
    if (initialValues) {
      const entries = [
        {
          tag: initialValues.tag || "",
          logged_hours: initialValues.logged_hours || "",
          description: initialValues.description || "",
        },
      ];
      form.reset({
        ...form.getValues(),
        ...initialValues,
        entries: (initialValues.entries && initialValues.entries.length > 0)
          ? initialValues.entries
          : entries,
      });
      if (initialValues.date) {
        setCurrentDate(initialValues.date);
      }
    }
  }, [initialValues]);

  useRemoveSearchParam("date", !!date);

  const handleClose = () => {
    navigate(-1);
  };

  if (!canEdit && date) {
    navigate(`/${TIMESHEET_LOG_DAY_VIEW}/${date}`);
  }

  const handleCreate = async (context) => {
    const values = context.values;
    const date = values.date;
    const selectedJob = jobs.find((job) => job.id === values.job);
    if ((selectedJob?.disable_job_logging) && !values.milestone) {
      form.setError("milestone", {
        type: "manual",
        message: "Select a milestone to log hours",
      });
      throw "select milestone first";
    }

    values.created_from_draft = created_from_draft;
    await logTimesheetMutation.mutateAsync({ values });

    if (onSuccess) {
      await onSuccess(values);
    }

    eventBus.emit("refresh", `timesheet-${date}`);
    eventBus.emit("refresh", `draft_timesheet-${date}`);

    return {
      date: date,
      project: "",
      job: "",
      milestone: "",
      entries: [
        {
          tag: "",
          logged_hours: "",
          description: "",
        },
      ],
    };
  };

  return (
    <div className="w-full h-full p-6">
      <Form
        form={form}
        schema={timesheetLogSchema}
        itemId={"+"}
        create={handleCreate}
        className="h-full"
      >
        {({ isCreating, createItem }) => {
          useHotkeys(
            "ctrl+s, cmd+s",
            (event) => {
              event.preventDefault();
              if (!isCreating) {
                setIsNew(false);
                createItem().then(handleClose);
              }
            },
            { preventDefault: true },
          );

          useHotkeys(
            "ctrl+shift+s, cmd+shift+s",
            (event) => {
              event.preventDefault();
              if (!isCreating) {
                setIsNew(true);
                createItem();
              }
            },
            { preventDefault: true },
          );
          return (
            <>
              <div className="flex gap-6 flex-col w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => {
                      // Check if current date exists in predefined options
                      const dateExistsInOptions = dateOptions.some(
                        (opt) => opt.value === field.value,
                      );
                      const hasDate = field.value && field.value.trim() !== "";

                      return (
                        <FormItem className="flex-1">
                          <FormLabel>Date</FormLabel>
                          <FormControl>
                            {!hasDate || dateExistsInOptions ? (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {dateOptions.map((opt) => (
                                    <SelectItem
                                      key={opt.offset}
                                      value={opt.value}
                                    >
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                                <span className="text-muted-foreground">
                                  {field.value
                                    ? formatDateForLogOptions(field.value)
                                    : "No date selected"}
                                </span>
                              </div>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="project"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Project</FormLabel>
                        <FormControl>
                          <TimesheetSelect
                            className="w-full max-w-none"
                            value={field.value}
                            onValueChange={(e) => {
                              field.onChange(e);
                              if (form.watch("job")) {
                                form.setValue("job", "");
                              }
                              if (form.watch("milestone")) {
                                form.setValue("milestone", "");
                              }
                            }}
                            items={projects}
                            groupLabel="Projects"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="job"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Job</FormLabel>
                        <FormControl>
                          <TimesheetSelect
                            className="w-full max-w-none"
                            value={field.value}
                            onValueChange={(e) => {
                              field.onChange(e);
                              if (form.watch("milestone")) {
                                form.setValue("milestone", "");
                              }
                            }}
                            items={jobs}
                            disabled={!form.getValues().project}
                            groupLabel="Jobs"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="milestone"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Milestone</FormLabel>
                        <FormControl>
                          <TimesheetSelect
                            className="w-full max-w-none"
                            value={field.value}
                            onValueChange={field.onChange}
                            items={milestones}
                            disabled={!form.getValues().job}
                            groupLabel="Milestones"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                </div>

                <hr className="border-gray" />

                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex gap-4 items-end w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1 items-start">
                        {/* Tag */}
                        <FormField
                          control={form.control}
                          name={`entries.${index}.tag`}
                          render={({ field: entryField }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Tag</FormLabel>
                              <FormControl>
                                <TimesheetSelect
                                  className="w-full max-w-none"
                                  value={entryField.value}
                                  onValueChange={entryField.onChange}
                                  items={tag}
                                  disabled={!form.getValues().job}
                                  groupLabel="Tag"
                                  displayProperty="label"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Hours */}
                        <FormField
                          control={form.control}
                          name={`entries.${index}.logged_hours`}
                          render={({ field: entryField }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Hours</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="ex. 6.5"
                                  {...entryField}
                                  step="0.5"
                                  min="0"
                                  max="24"
                                  onWheel={(e) => e.target.blur()}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "") {
                                      entryField.onChange(e);
                                      return;
                                    }

                                    if (value < 0 || (value * 10) % 5 !== 0) {
                                      e.target.value = "";
                                      return;
                                    }
                                    entryField.onChange(e);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Description */}
                        <FormField
                          control={form.control}
                          name={`entries.${index}.description`}
                          render={({ field: entryField }) => (
                            <FormItem className="col-span-1 md:col-span-2 flex flex-col w-full h-full">
                              <FormLabel>Description</FormLabel>
                              <FormControl>
                                <Textarea
                                  className="min-h-24 resize-y"
                                  style={{ fieldSizing: "fixed" }}
                                  value={entryField.value}
                                  onChange={entryField.onChange}
                                  {...entryField}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Delete Button */}
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 hover:border-destructive/20 hover:bg-destructive/10 hover:text-destructive/90 flex items-center justify-center shrink-0"
                          onClick={() => remove(index)}
                        >
                          <Icon icon={DELETE} size={4} />
                        </Button>
                      )}
                    </div>
                  ))}

                  {/* Add Entry Button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed border-2 py-6 flex items-center justify-center gap-2 hover:bg-muted"
                    onClick={() => append({ tag: "", logged_hours: "", description: "" })}
                  >
                    <Icon icon={PLUS} size={4} /> Add Entry
                  </Button>
                </div>
              </div>

              <div>
                {!command ? (
                  <HeaderPortal>
                    <div className="space-x-2">
                      {!onDelete && (
                        <ButtonWithLoading
                          isLoading={isCreating && isNew}
                          onClick={() => {
                            setIsNew(true);
                            createItem();
                          }}
                          type="button"
                        >
                          Save and Create{" "}
                          {/* <span className="text-xs opacity-70 ml-1">
                        (ctrl + ⇧ + S)
                      </span> */}
                        </ButtonWithLoading>
                      )}
                      {onDelete && (
                        <>
                          <ButtonWithLoading
                            isLoading={isCreating && isNew}
                            onClick={() => {
                              setShowDeleteDialog(true);
                            }}
                            type="button"
                            variant="destructive"
                          >
                            Delete
                          </ButtonWithLoading>
                          <Dialog
                            open={showDeleteDialog}
                            onOpenChange={setShowDeleteDialog}
                          >
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Are you sure?</DialogTitle>
                                <DialogDescription>
                                  Are you sure you want to delete this log? This
                                  action cannot be undone.
                                </DialogDescription>
                              </DialogHeader>
                              <DialogFooter className="gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => setShowDeleteDialog(false)}
                                >
                                  Cancel
                                </Button>
                                <ButtonWithLoading
                                  variant="destructive"
                                  onClick={() => {
                                    onDelete();
                                    setShowDeleteDialog(false);
                                  }}
                                >
                                  Delete
                                </ButtonWithLoading>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                      <ButtonWithLoading
                        variant="default"
                        isLoading={isCreating && !isNew}
                        onClick={() => {
                          setIsNew(false);
                          createItem().then(handleClose);
                        }}
                      >
                        Save{" "}
                        {/* <span className="text-xs opacity-70 ml-1">
                        (ctrl + S)
                      </span> */}
                      </ButtonWithLoading>
                    </div>
                  </HeaderPortal>
                ) : (
                  <div className="space-x-2 flex justify-end">
                    {!onDelete && (
                      <ButtonWithLoading
                        isLoading={isCreating && isNew}
                        onClick={() => {
                          setIsNew(true);
                          createItem();
                        }}
                        type="button"
                      >
                        Save and Create{" "}
                        {/* <span className="text-xs opacity-70 ml-1">
                        (ctrl + ⇧ + S)
                      </span> */}
                      </ButtonWithLoading>
                    )}
                    {onDelete && (
                      <>
                        <ButtonWithLoading
                          isLoading={isCreating && isNew}
                          onClick={() => {
                            setShowDeleteDialog(true);
                          }}
                          type="button"
                          variant="destructive"
                        >
                          Delete
                        </ButtonWithLoading>
                        <Dialog
                          open={showDeleteDialog}
                          onOpenChange={setShowDeleteDialog}
                        >
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Are you sure?</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete this log? This
                                action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter className="gap-2">
                              <Button
                                variant="outline"
                                onClick={() => setShowDeleteDialog(false)}
                              >
                                Cancel
                              </Button>
                              <ButtonWithLoading
                                variant="destructive"
                                onClick={() => {
                                  onDelete();
                                  setShowDeleteDialog(false);
                                }}
                              >
                                Delete
                              </ButtonWithLoading>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                    <ButtonWithLoading
                      variant="default"
                      isLoading={isCreating && !isNew}
                      onClick={() => {
                        setIsNew(false);
                        createItem().then(handleClose);
                      }}
                    >
                      Save{" "}
                    </ButtonWithLoading>
                  </div>
                )}
              </div>
            </>
          );
        }}
      </Form>
    </div>
  );
}
